/**
 * Two-state switchers built into the TradingView header.
 *
 * DexScreener puts `Price / Mcap` and the quote currency right in the chart's
 * own toolbar, next to the resolution tabs, and that is the right place for
 * them: they change what the price axis *means*, so they belong with the other
 * controls that change what is drawn — not in a separate strip above the chart
 * that scrolls independently and can drift out of sync with it.
 *
 * `createButton` hands back a bare element in the header's flow; everything
 * below is the styling and behaviour that makes two of them read as one
 * segmented control.
 */

export interface SwitcherOption<T extends string> {
	value: T
	label: string
}

/**
 * Ordinals Wallet purple, not TradingView blue.
 *
 * The library defaults its active-control colour to its own brand blue, which
 * appears nowhere else on this site — so an untouched switcher reads as a
 * third-party widget bolted into the page. `--primary-500` is the accent every
 * other active control here uses.
 *
 * Read from the host document rather than hardcoded, so a theme change moves
 * the chart with it; the literals are the fallback for server rendering and
 * for a document that has not defined the token.
 */
function headerColors(): { active: string; idle: string } {
	const fallback = { active: '#9E77ED', idle: '#787b86' }
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return fallback
	}
	try {
		const root = getComputedStyle(document.documentElement)
		const active = root.getPropertyValue('--primary-500').trim()
		const idle = root.getPropertyValue('--muted-foreground').trim()
		return {
			active: active || fallback.active,
			// `--muted-foreground` is bare HSL components (Tailwind's
			// convention), which only compose into a colour inside hsl().
			idle: idle ? `hsl(${idle.replace(/\s+/g, ', ')})` : fallback.idle
		}
	} catch {
		return fallback
	}
}

/**
 * Render a `A / B` switcher into a header button element.
 *
 * Returns a setter so the caller can reflect state changed elsewhere (a
 * keyboard shortcut, a linked control) without rebuilding the button.
 */
export function renderSwitcher<T extends string>(
	host: HTMLElement,
	options: SwitcherOption<T>[],
	initial: T,
	onChange: (value: T) => void,
	ariaLabel: string
): (value: T) => void {
	const { active, idle } = headerColors()

	host.style.display = 'inline-flex'
	host.style.alignItems = 'center'
	host.style.gap = '4px'
	host.style.cursor = 'default'
	host.setAttribute('role', 'group')
	host.setAttribute('aria-label', ariaLabel)

	let current = initial
	const buttons = new Map<T, HTMLSpanElement>()

	const paint = () => {
		for (const [value, el] of buttons) {
			const on = value === current
			el.style.color = on ? active : idle
			el.style.fontWeight = on ? '600' : '400'
			el.setAttribute('aria-pressed', String(on))
		}
	}

	options.forEach((option, i) => {
		if (i > 0) {
			const sep = document.createElement('span')
			sep.textContent = '/'
			sep.style.color = idle
			sep.style.opacity = '0.5'
			sep.setAttribute('aria-hidden', 'true')
			host.appendChild(sep)
		}

		const el = document.createElement('span')
		el.textContent = option.label
		el.tabIndex = 0
		el.setAttribute('role', 'button')
		el.style.cursor = 'pointer'
		el.style.userSelect = 'none'
		el.style.transition = 'color 120ms'

		const choose = () => {
			if (current === option.value) {
				return
			}
			current = option.value
			paint()
			onChange(option.value)
		}
		el.addEventListener('click', choose)
		// A control reachable by mouse and not by keyboard is a broken control.
		el.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault()
				choose()
			}
		})

		buttons.set(option.value, el)
		host.appendChild(el)
	})

	paint()
	return (value: T) => {
		current = value
		paint()
	}
}

